import React, { FC, useCallback, useMemo } from 'react'
import { Button } from 'transfix-ui/components/Button'
import { ConfirmationModal, IModalAction } from 'transfix-ui/components/Modals'
import { useGlobalToast } from 'transfix-ui/components/Toast'
import { Typography } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import { IShipmentStopAppointmentStatuses } from 'types/graphqlTypes'
import { useModal } from 'utils/hooks/useModal'
import { IShipmentProgressStopFragment } from '../ShipmentProgress/graphql/ShipmentProgressStopFragment'
import { IStopFragment } from '../ShipmentSchedulingTab/graphql/ShipmentStopFragment'
import { useTenderShipmentMutation } from '../ShipmentSchedulingTab/graphql/TenderShipmentMutation'

type IStop = IStopFragment | IShipmentProgressStopFragment

interface IRemoveHoldButtonProps {
  shipmentId: string
  stops?: IStop[]
}

const removeHoldConfirmation = 'removeHoldConfirmation'

const getAppointmentsConfirmed = (stops?: IStop[]): boolean => {
  let isPickupApptConfirmed = false
  let isDeliveryApptConfirmed = false

  if (stops) {
    const [firstPickup] = stops
    const lastDelivery = stops[stops.length - 1]
    const { status: pickup = '' } = firstPickup?.appointment || {}
    const { status: delivery = '' } = lastDelivery?.appointment || {}

    isPickupApptConfirmed = pickup === IShipmentStopAppointmentStatuses.confirmed
    isDeliveryApptConfirmed = delivery === IShipmentStopAppointmentStatuses.confirmed
  }

  return isPickupApptConfirmed && isDeliveryApptConfirmed
}

export const RemoveHoldButton: FC<IRemoveHoldButtonProps> = ({ shipmentId, stops }) => {
  const { openToast } = useGlobalToast()
  const { closeModal, isOpen, openModal } = useModal()
  const [removeHold] = useTenderShipmentMutation()
  const areAppointmentsConfirmed = getAppointmentsConfirmed(stops)

  const openRemoveHoldConfirmationModal = useMemo(() => openModal(removeHoldConfirmation), [
    openModal,
  ])

  const removeShipmentHold = useCallback(async () => {
    try {
      const { errors } = await removeHold({
        variables: {
          input: {
            id: shipmentId,
          },
        },
      })

      const [error] = errors || []

      if (error) {
        throw error
      }

      openToast({
        toastMessage: 'Shipment Status Updated.',
      })
    } catch (error) {
      openToast({
        toastMessage: error?.message,
        toastType: 'alert',
      })
    } finally {
      closeModal()
    }
  }, [closeModal, openToast, removeHold, shipmentId])

  const removeHoldClickHandler = useCallback(() => {
    if (areAppointmentsConfirmed) {
      removeShipmentHold()
    } else {
      openRemoveHoldConfirmationModal()
    }
  }, [areAppointmentsConfirmed, openRemoveHoldConfirmationModal, removeShipmentHold])

  const actions: IModalAction[] = useMemo(() => {
    return [
      {
        label: 'Cancel',
        type: 'dismiss',
      },
      {
        label: 'Yes, Take it off hold',
        type: 'confirm',
        action: removeShipmentHold,
        dataTest: 'submit-modal',
      },
    ]
  }, [removeShipmentHold])

  return (
    <Box mr={1}>
      <Button color='tertiary' onClick={removeHoldClickHandler} data-test='remove-hold'>
        Remove Hold
      </Button>
      {isOpen(removeHoldConfirmation) && (
        <ConfirmationModal title='Remove Hold' onClose={closeModal} size={4} open actions={actions}>
          <Box>
            <Typography variant='body1'>
              Are you sure you want to remove On Hold without a confirmed appointment?
            </Typography>
          </Box>
        </ConfirmationModal>
      )}
    </Box>
  )
}
